import privileges from '@ubeya/shared-web/privileges';
import createCachedSelector from 're-reselect';
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import * as api from '../../services/api';
import useAccount from './useAccount';

const adminPrivilegesIds = [privileges.ACCOUNT_ADMIN, privileges.BRANCH_ADMIN];

const selector = createCachedSelector(
  (data) => data.data,
  (data) => {
    const accountRolesRaw = data || [];

    const accountRoles = accountRolesRaw.map(({ privilegesIds, ...accountRoleRaw }) => {
      const adminTypeId = privilegesIds.find((privilegeId) => adminPrivilegesIds.includes(privilegeId));
      return {
        ...accountRoleRaw,
        privilegesIds,
        privilegesWithoutAdmin: privilegesIds
          .filter((privilegeId) => privilegeId !== adminTypeId)
          .map((id) => ({
            id
          })),
        adminTypeId
      };
    });

    const mappedAccountRoles = (accountRoles || []).reduce((acc, accountRole) => {
      acc[accountRole.id] = accountRole;
      return acc;
    }, {});
    return { accountRoles, mappedAccountRoles };
  }
)({
  keySelector: (data, storeKey) => `${storeKey.join('#')}`
});

const useAccountRoles = () => {
  const { accountId } = useAccount();
  const storeKey = useMemo(() => ['account-roles', accountId], [accountId]);
  const queryClient = useQueryClient();

  const { isLoading, data, refetch } = useQuery(storeKey, () => api.fetchAccountRoles({ accountId }), {
    enabled: !!accountId,
    select: (accountRolesData) => selector(accountRolesData, storeKey)
  });

  const { accountRoles = [], mappedAccountRoles = {} } = data || {};

  const { mutateAsync: createAccountRole } = useMutation((params) => api.createAccountRole({ accountId, ...params }), {
    onSuccess: () => queryClient.invalidateQueries(storeKey)
  });

  const { mutateAsync: updateAccountRole } = useMutation((params) => api.updateAccountRole({ accountId, ...params }), {
    onSuccess: () => queryClient.invalidateQueries(storeKey)
  });

  const { mutateAsync: deleteAccountRole } = useMutation((params) => api.deleteAccountRole({ accountId, ...params }), {
    onSuccess: () => queryClient.invalidateQueries(storeKey)
  });

  return {
    isLoading,
    accountRoles,
    mappedAccountRoles,
    refetch,
    createAccountRole,
    updateAccountRole,
    deleteAccountRole
  };
};

export default useAccountRoles;
